import { useCallback, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useErrorHandler } from '../useErrorHandler'
import { useResponseHandler } from '../useResponseHandler'
import { useAuth, useTenant } from '../index'

const { env } = window
const PER_PAGE = 10

const useProjectUsersSearch = ({
  affiliationId,
  search,
  userType,
  page,
  sort
}) => {
  const { securedFetch } = useAuth()
  const { handleError } = useErrorHandler()
  const { handleResponse } = useResponseHandler()
  const { tenant } = useTenant()

  const getUsers = async (tenantId) => {
    const offset = (page - 1) * PER_PAGE
    const sortParams = sort.reduce((result, item) => {
      result += `&sort=${item.id}:${item.desc ? 'desc' : 'asc'}`
      return result
    }, '')
    const searchParam = search ? `&search=${search}` : ''
    const userTypeParam = userType ? `&userType=${userType}` : ''
    const affiliationParam = affiliationId
      ? `&affiliationId=${affiliationId}`
      : ''
    const url = encodeURI(
      `${env.apiUrl}/tenants/${tenantId}/manageUsers?max=${PER_PAGE}` +
        `&offset=${offset}${sortParams}${userTypeParam}${searchParam}${affiliationParam}`
    )
    const response = await securedFetch(url)
    await handleResponse(response)
    return response.json()
  }

  const query = useQuery({
    queryKey: [
      'project users',
      tenant?.id,
      affiliationId,
      search,
      userType,
      page,
      sort
    ],
    queryFn: () => getUsers(tenant?.id),
    enabled: !!tenant && false,
    gcTime: 0
  })

  useEffect(() => {
    if (query.isError) {
      handleError(query.error, 'Error getting users')
    }
  }, [query.isError, query.error, handleError])

  const safeRefetch = useCallback(() => {
    if (tenant?.id) {
      return query.refetch()
    }
    return Promise.resolve()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.id])

  return {
    users: query.data ? query.data.items : undefined,
    pageCount: query.data ? Math.ceil(query.data.count / PER_PAGE) : undefined,
    refetch: safeRefetch
  }
}

export { useProjectUsersSearch }
